import type { SearchResult } from '@/services/search';

/**
 * Which field a master-search suggestion matched on (SCRUM-1565).
 *
 * The BE `SearchEntity` carries no `matchedField` — only `matchedText`, the
 * value of the first Atlas highlight (or the client number / empty string,
 * see below). So the label has to be inferred, and the shape of the text alone
 * is not enough: a client number and a legacy bare-digit mobile look the same.
 * The second signal is the query the agent typed, which is why every helper
 * here takes it.
 *
 * What each BE branch returns as `matchedText` (verified on dev 2026-09-21):
 *  - client number  → exactly the digit token typed (exact `clientNumber` lookup)
 *  - mobile         → the FULL stored number the token is a prefix of
 *                     (`+61…` canonical; pre-Aug-2026 migrations left `61…`)
 *  - user id        → the id itself
 *  - name / email   → that field's value
 *  - DOB only       → '' (the DOB is a filter, so Atlas produces no highlight)
 */
export type MatchLabel =
  | 'Client Number'
  | 'Customer ID'
  | 'DOB'
  | 'Email'
  | 'Mobile'
  | 'Name';

/** Honoured if the BE ever starts sending `matchedField` (the FE type already allows it). */
const FIELD_LABELS: Record<string, MatchLabel> = {
  clientNumber: 'Client Number',
  dateOfBirth: 'DOB',
  email: 'Email',
  firstName: 'Name',
  lastName: 'Name',
  mobileNumber: 'Mobile',
  userId: 'Customer ID',
  userName: 'Email',
};

/** What the BE parses as a client number: digits with no leading zero. */
const BARE_NUMBER = /^[1-9]\d*$/;
/**
 * A complete Australian mobile as bare digits. `04…` never gets here (leading
 * zero), so the two shapes are 4 + 8 digits (local without the 0, how the
 * synthetic dev seeds are stored) and 614 + 8 digits (legacy migrated rows).
 * No client number has either shape (6–8 digits, or 10 starting 1–3).
 */
const FULL_AU_MOBILE_BARE = /^(?:614\d{8}|4\d{8})$/;
const MOBILE_LIKE = /^\+?\d[\d\s-]{6,}$/;
const DATE_TOKEN = /^\d{1,4}[/-]\d{1,2}[/-]\d{1,4}$/;

function tokenize(query: string): string[] {
  return query.trim().split(/\s+/).filter(Boolean);
}

function dateTokenOf(query: string): string | undefined {
  return tokenize(query).find(token => DATE_TOKEN.test(token));
}

export function getMatchLabel(result: SearchResult, query = ''): string {
  if (result.matchedField) {
    return FIELD_LABELS[result.matchedField] ?? result.matchedField;
  }

  const text = result.matchedText;
  if (text.includes('@')) return 'Email';
  if (text !== '' && text === result.userId) return 'Customer ID';
  if (text === '') return dateTokenOf(query) ? 'DOB' : 'Name';

  // The client-number branch echoes the typed token exactly; the mobile branch
  // returns the whole stored number, which equals the token only when the
  // agent typed a complete mobile — and a complete mobile has a shape no
  // client number has.
  if (
    BARE_NUMBER.test(text) &&
    tokenize(query).includes(text) &&
    !FULL_AU_MOBILE_BARE.test(text)
  ) {
    return 'Client Number';
  }
  if (MOBILE_LIKE.test(text)) return 'Mobile';
  return 'Name';
}

/**
 * The value to print after the label. Normally `matchedText`; for a DOB-only
 * hit the BE sends '' so the date the agent typed is shown instead, and if
 * there is nothing else to show the customer's name stands in.
 */
export function getMatchDisplayText(result: SearchResult, query = ''): string {
  if (result.matchedText !== '') return result.matchedText;
  return (
    dateTokenOf(query) ??
    `${result.firstName ?? ''} ${result.lastName ?? ''}`.trim()
  );
}
